<script>
  import cloneDeep from 'lodash-es/cloneDeep';
  import merge from 'lodash-es/merge';
  import { nextTick, ref, toRefs } from 'vue';

  import useValidation from '../../composables/useValidation/useValidation';
  import { LLLV_CHANGE_TRIGGERS } from '../../constants';
  import { t } from '../../locale';
  import { persistentStorage } from '../../storage';
  import Button from '../Button/Button.vue';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import DatePicker from '../DatePicker/DatePicker.vue';
  import Input from '../Input/Input.vue';
  import InputOptions from '../InputOptions/InputOptions.vue';
  import Radio from '../Radio/Radio.vue';
  import Select from '../Select/Select.vue';

  export default {
    name: 'll-filters',

    components: {
      'll-button': Button,
      'll-checkbox': Checkbox,
      'll-input': Input,
      'll-input-options': InputOptions,
      'll-radio': Radio,
      'll-select': Select,
      DatePicker,
    },

    props: {
      /**
       * Whether to disable the Apply button
       */
      disableApply: Boolean,
      /**
       * Whether to disable the Clear button
       */
      disableClear: Boolean,
      /**
       * Whether to disable the sticky filters
       * If disabled, a default pre-selected value(s) can be added to
       * the filter component's attributes
       */
      disablePersistency: Boolean,
      /**
       * Filters schema
       */
      schema: {
        type: Array,
        default: () => [],
      },
      /**
       * Validation schema function that returns an object
       */
      validationSchema: {
        type: Function,
        default: () => () => undefined,
      },
    },

    emits: ['change', 'submit', 'reset'],

    setup(props) {
      const { validationSchema: getValidationSchema } = toRefs(props);
      const validationSchema = getValidationSchema.value();
      const validationValues = ref({});

      // Without ref, `this.validation` will always evaluate to its initial value of `undefined` due to lack of reactivity.
      const validation = ref();

      function initValidation() {
        if (validationSchema?.toString() !== '[object Object]') {
          return;
        }

        validationValues.value = Object.keys(validationSchema).reduce((values, fieldName) => {
          values[fieldName] = undefined;

          return values;
        }, {});

        const validationRules = Object.entries(validationSchema).reduce((rules, [fieldName, fieldRules]) => {
          rules[fieldName] = fieldRules.map((fieldRule) => {
            return fieldRule(validationValues);
          });

          return rules;
        }, {});

        validation.value = useValidation({ rules: validationRules, values: validationValues });
        validation.value.validate();
      }

      return {
        validation,
        validationValues,
        initValidation,
      };
    },

    data() {
      return {
        applyText: t('ll.apply'),
        clearText: t('ll.clear'),
        filters: {},
        filterValues: {},
        forceRenderCount: 0,
      };
    },

    watch: {
      filterValues: {
        /**
         * Maps filters to a partcular object structure:
         * @param {IFilterSchemaItem} newFilterValues all selected filters
         * @returns {IFilter}
         */
        handler: function (newFilterValues) {
          const filters = Object.keys(newFilterValues).reduce((acc, i) => {
            const schema = this.schema[i];

            if (!schema || !schema.fieldToFilter) {
              return acc;
            }

            const item = newFilterValues[i];

            // Ignore empty string or undefined/null filter values
            if (item === '' || item == null) {
              return acc;
            }

            const trackBy = schema.attributes.trackBy || 'id';
            const isObject = typeof item === 'object';
            const isCustomFilterComponent = typeof schema.type === 'object';
            // TODO: remove handling of schema.attributes.multiple when we remove the vue-multiselect version of Select
            const isMultiple =
              (isCustomFilterComponent && schema.attributes.multiple) ||
              (!isCustomFilterComponent &&
                schema.type.includes('ll-select') &&
                (schema.attributes.multiple || !schema.attributes.single));
            const filterBy = isMultiple
              ? // local storage might have item stored as an object from the old Select
                (Array.isArray(item) ? item : [item]).map((v) => v[trackBy])
              : isObject
                ? [item[trackBy]]
                : [item];

            const groupedWith = schema.groupedWith;

            if (groupedWith) {
              filterBy[0] = {
                index: groupedWith,
                value: newFilterValues[groupedWith],
              };
              filterBy.push({
                index: Number(i),
                value: newFilterValues[i],
              });
            }

            if (filterBy.length) {
              acc[schema.fieldToFilter] = {
                // don't add to `filters` if no filterBy
                filterBy,
                filterType: schema.type, // Informs the applied filter count
                isMultiple,
                ...('fn' in schema && { customFilter: schema.fn }),
              };
            }

            return acc;
          }, {});

          this.filters = filters;
          this.$emit('change', filters);
        },

        deep: true,
        immediate: true,
      },

      filters() {
        if (this.validation) {
          this.updateValidationValues();
        }
      },
    },

    async created() {
      // initFilterValues updates the filterValues so using await nextTick() forces the code
      // after it to wait until nextTick is done, at which point the filterValues will
      // be done initializing. Otherwise, applyFilters sometimes applies the wrong
      // filters because filterValues is not done updating.
      this.initFilterValues();
      await nextTick();
      this.initValidation();
      this.applyFilters({ trigger: LLLV_CHANGE_TRIGGERS.LOAD });
    },

    methods: {
      initFilterValues() {
        if (this.disablePersistency) {
          this.filterValues = this.getDefaultFilterValues();

          return;
        }

        this.filterValues = this.getPersistentFilterValues() || this.getDefaultFilterValues();
      },

      /**
       * Apply the selected filters and save them to localStorage.
       * ListView calls this method using a ref to Filters.
       * The trigger option is used in ListView.
       * @param {object} [options] - optional parameters
       * @param {string} [options.trigger] - the action that triggered this function call
       */
      async applyFilters({ trigger = LLLV_CHANGE_TRIGGERS.APPLY } = {}) {
        if (this.validation) {
          await this.validation.validate();

          if (this.validation.hasErrors) {
            return;
          }
        }

        if (!this.disablePersistency) {
          this.persistFilterValues(this.filterValues);
        }

        this.$emit('submit', { trigger });
      },

      resetFilters() {
        this.filterValues = this.getDefaultFilterValues();

        this.forceRenderCount++;

        if (!this.disablePersistency) {
          this.persistFilterValues(this.filterValues);
        }

        // Emit after filters are updated
        this.$nextTick(() => {
          this.$emit('reset');
        });
      },

      /**
       * Returns pre-defined filter values to become the default values
       */
      getDefaultFilterValues() {
        return this.schema.reduce((acc, item, filterIndex) => {
          if (item.type === 'll-checkbox') {
            acc[filterIndex] = item.attributes.checked || false;
          } else if (Object.prototype.hasOwnProperty.call(item.attributes, 'modelValue')) {
            acc[filterIndex] = item.attributes.modelValue;
          }

          return acc;
        }, {});
      },

      /**
       * Intended for external use, when filter values need to be set for the user
       */
      setFilterValues(newValues) {
        this.filterValues = merge({}, this.filterValues, newValues);
        this.forceRenderCount++;
      },

      updateValidationValues() {
        const validatable = Object.keys(this.validationSchema());

        this.schema.forEach((fieldSchema, filterIndex) => {
          const fieldToFilter = fieldSchema.fieldToFilter;
          const validate = validatable.includes(fieldToFilter);

          if (validate) {
            const filter = this.filters[fieldToFilter];

            /**
             * Get all validatable fields.
             * Use parsed value of filters that are set,
             * otherwise fall back to `filterValue` value if not set.
             */
            const filterValue = filter
              ? filter.isMultiple
                ? filter.filterBy
                : filter.filterBy[0]
              : this.filterValues[filterIndex];

            this.validationValues[fieldToFilter] = filterValue;
          }
        });
      },

      /**
       * Change handler for filter fields
       */
      onChange(filter, filterValues, filterIndex) {
        if (filter.on?.change) {
          // Maps to `on.change` handlers in the filter schema.
          filter.on.change(filterValues, filterIndex);
        }
      },

      /**
       * Select input event handler for filter fields
       */
      onSelectInput(newValue, filter, filterIndex) {
        if (newValue === undefined) {
          delete this.filterValues[filterIndex]; // prevent undefined filterValues which cause an empty result list
        } else {
          this.filterValues[filterIndex] = newValue;
        }

        this.onChange(filter, this.filterValues, filterIndex);
      },

      /**
       * Get filter values from local storage.
       * @returns {any}
       */
      getPersistentFilterValues() {
        return persistentStorage.get('filter-values');
      },

      /**
       * Clone new filterValues to remove reactivity, and save them to localStorage.
       * @param {object} newValues - the filterValues to be persisted in localStorage
       */
      persistFilterValues(newValues) {
        persistentStorage.set('filter-values', cloneDeep(newValues));
      },
    },
  };
</script>

<template>
  <div class="ll-grid grid-cols-4 md:grid-cols-12">
    <template v-for="(filter, filterIndex) in schema">
      <div
        v-if="!filter.hidden"
        :key="`${filterIndex} ${forceRenderCount}`"
        class="field-wrapper col-span-4 md:col-span-3"
        :class="{ 'max-md:hidden': !filter.type, 'flex items-end': filter.type === 'll-checkbox' }"
        :data-test="`filter|${filter.fieldToFilter}`"
      >
        <!-- Filter component -->
        <ll-select
          v-if="filter.type === 'll-select'"
          v-bind="filter.attributes"
          :error="validation?.fields[filter.fieldToFilter] && validation?.getError(filter.fieldToFilter)"
          :label="filter.label"
          :model-value="filterValues[filterIndex]"
          @update:model-value="(newValue) => onSelectInput(newValue, filter, filterIndex)"
        />
        <ll-checkbox
          v-else-if="filter.type === 'll-checkbox'"
          v-model:checked="filterValues[filterIndex]"
          v-bind="filter.attributes"
          :error-text="validation?.fields[filter.fieldToFilter] && validation?.getError(filter.fieldToFilter)"
          :label="filter.label"
          @update:checked="onChange(filter, filterValues, filterIndex)"
        />
        <ll-radio
          v-else-if="filter.type === 'll-radio'"
          v-model:checked="filterValues[filterIndex]"
          v-bind="filter.attributes"
          :error-text="validation?.fields[filter.fieldToFilter] && validation?.getError(filter.fieldToFilter)"
          :label="filter.label"
          @update:checked="onChange(filter, filterValues, filterIndex)"
        />
        <component
          v-bind="filter.attributes"
          :is="filter.type"
          v-else-if="filter.type"
          v-model="filterValues[filterIndex]"
          :error-text="validation?.fields[filter.fieldToFilter] && validation?.getError(filter.fieldToFilter)"
          :label="filter.label"
          @update:model-value="onChange(filter, filterValues, filterIndex)"
        >
          <template v-if="filter.slots && filter.slots.selected" #selected="{ option }">
            <component :is="filter.slots.selected" v-bind="option.props" />
          </template>
          <template v-if="filter.slots && filter.slots.option" #option="{ option }">
            <component :is="filter.slots.option" v-bind="option.props" />
          </template>
          <template v-if="filter.slots && filter.slots.prepend" #prepend>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="filter.slots.prepend"></span>
          </template>
          <template v-if="filter.slots && filter.slots.append" #append>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="filter.slots.append"></span>
          </template>
        </component>
      </div>
    </template>
    <div
      class="button-grid col-span-4 flex items-end justify-end md:col-span-12"
      :class="$style['filter-button-group']"
    >
      <ll-button secondary data-test="button|reset-filters" :disabled="disableClear" @click="resetFilters">
        {{ clearText }}
      </ll-button>
      <ll-button
        primary
        data-test="button|apply-filters"
        :disabled="disableApply || validation?.hasErrors"
        @click="applyFilters"
      >
        {{ applyText }}
      </ll-button>
    </div>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .filter-button-group {
      @media (width <= theme(--breakpoint-md)) {
        display: flex;
        flex-direction: column-reverse;
      }

      button {
        @media (width <= theme(--breakpoint-md)) {
          width: 100% !important;
        }

        &:nth-child(2) {
          @media (width <= theme(--breakpoint-md)) {
            margin-bottom: --spacing(3);
          }
        }
      }
    }
  }
</style>
